import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router'
import RegistrationInfo from './Steps/RegistrationInfo'
import ClinicalHistory from './Steps/ClinicalHistory'
import ProceduresAndOpme from './Steps/ProceduresAndOpme'
import ReportsAndDocuments from './Steps/ReportsAndDocuments'
import {
  ActionBar,
  ButtonGroup,
  HideParentScrolls,
  LayoutMainContent,
  LayoutReview,
  LayoutWrapper,
  MobileReviewButton,
  ReviewButtonGroup,
  // SaveDraftReviewButton,
  WizardContainer
} from './components/styles'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import ReviewSurgicalOrder from 'presentation/doctor/components/ReviewSurgicalOrder'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { useStores } from 'presentation/hooks/use-stores'
import { observer } from 'mobx-react-lite'
import {
  ORDERED_STEPS,
  OrderStepsPath
} from 'presentation/contexts/surgical-order-v2/types'
import Wizard from 'presentation/shared/components/Wizard'
import ResponsiveReviewSurgicalOrder from 'presentation/shared/components/ResponsiveReviewSurgicalOrder'

const BASE_PATH = '/pedido'
const FALLBACK_PATH = OrderStepsPath.REGISTRATION

const STEP_CONFIG: Record<
  OrderStepsPath,
  { component: React.ComponentType; label: string }
> = {
  [OrderStepsPath.REGISTRATION]: {
    component: RegistrationInfo,
    label: 'Informações cadastrais'
  },
  [OrderStepsPath.PROCEDURE]: {
    component: ProceduresAndOpme,
    label: 'Procedimentos e OPME'
  },
  [OrderStepsPath.CLINICAL_HISTORY]: {
    component: ClinicalHistory,
    label: 'História e ficha clínica'
  },
  [OrderStepsPath.REPORTS_AND_DOCUMENTS]: {
    component: ReportsAndDocuments,
    label: 'Laudos e documentos'
  }
}

const ROUTES = ORDERED_STEPS.map((path) => ({
  path,
  exact: true,
  ...STEP_CONFIG[path]
}))

const AddSurgicalOrderV2Layout = observer(() => {
  const history = useHistory()
  const location = useLocation()
  const { surgicalOrderV2: store } = useStores()
  const [showReview, setShowReview] = useState(false)
  const [actionBarHeight, setActionBarHeight] = useState(0)
  const wizardSteps = ROUTES.map((r) => r.label)
  const activeWizardStep = ROUTES.findIndex((r) => r.path === store.currentStep)

  const actionBarRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const observer = new ResizeObserver(([entry]) => {
      setActionBarHeight(entry.contentRect.height + 32)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const handleBack = () => {
    store.resetSubmitTrigger()
    store.resetSaveOnlyTrigger()
    const prevIndex = store.currentStepIndex - 1
    if (prevIndex >= 0) {
      history.replace(ORDERED_STEPS[prevIndex])
    } else {
      history.goBack()
    }
  }

  const handleSave = () => {
    store.triggerSaveOnly()
  }

  const handleNext = () => {
    store.triggerSubmit()
  }

  // Limpa Store ao iniciar fluxo (useLayoutEffect garante execução ANTES de qualquer useEffect filho)
  useLayoutEffect(() => {
    store.clear()
  }, [])

  // Redireciona para o primeiro step
  useEffect(() => {
    if (location.pathname !== FALLBACK_PATH) {
      history.replace(FALLBACK_PATH, location.state)
    }
  }, [])

  useEffect(() => {
    // Pegamos o pathname e setamos na store.
    store.setCurrentStep(location.pathname as any)
  }, [location.pathname, store])

  return (
    <>
      <HideParentScrolls />
      <HeaderNew
        actualPageTitle="Criar pedido cirúrgico"
        actualPageOnClick={handleBack}
      />
      <ContainerNew
        style={{
          paddingTop: '0px',
          paddingBottom: '0px'
        }}
      >
        <WizardContainer position="top">
          <Wizard
            activeStep={activeWizardStep}
            steps={wizardSteps}
            nonLinear
            completedSteps={store.completedSteps}
          />
        </WizardContainer>

        <ReviewButtonGroup>
          <MobileReviewButton onClick={() => setShowReview(true)}>
            Ver resumo do pedido
          </MobileReviewButton>
          {/* <SaveDraftReviewButton
            onClick={() => console.log('Salvar alterações')}
          >
            Salvar alterações
          </SaveDraftReviewButton> */}
        </ReviewButtonGroup>

        <LayoutWrapper actionBarHeight={actionBarHeight}>
          <LayoutMainContent>
            <Switch>
              {ROUTES.map((route) => (
                <Route
                  key={route.path}
                  exact={route.exact}
                  path={route.path}
                  component={route.component}
                />
              ))}
              <Route path={`${BASE_PATH}*`}>
                <Redirect to={FALLBACK_PATH} />
              </Route>
            </Switch>
          </LayoutMainContent>

          <LayoutReview>
            <ResponsiveReviewSurgicalOrder
              title="Resumo do pedido"
              description="Revise as informações do pedido cirúrgico preenchidas até o momento. Ao final do preenchimento envie para análise pela CRMO"
              isOpen={showReview}
              onClose={() => setShowReview(false)}
            >
              <ReviewSurgicalOrder setShowReview={setShowReview} />
            </ResponsiveReviewSurgicalOrder>
          </LayoutReview>
        </LayoutWrapper>

        <ActionBar ref={actionBarRef}>
          <WizardContainer position="bottom">
            <Wizard
              activeStep={activeWizardStep}
              steps={wizardSteps}
              nonLinear
              completedSteps={store.completedSteps}
            />
          </WizardContainer>
          <ButtonGroup>
            <ButtonNew outlined onMouseDown={handleBack}>
              Voltar
            </ButtonNew>
            {!store.isFirstStep && (
              <ButtonNew outlined onMouseDown={handleSave}>
                Salvar alterações
              </ButtonNew>
            )}
            <ButtonNew onMouseDown={handleNext}>
              {store.isLastStep ? 'Enviar pedido' : 'Próximo'}
            </ButtonNew>
          </ButtonGroup>
        </ActionBar>
      </ContainerNew>
    </>
  )
})

export default AddSurgicalOrderV2Layout
